/**
 * Remote students (onboarding records).
 */

import { apiClient } from '@/services/api-client';
import type {
  StudentRecord,
  OnboardingFilters as StudentFilters,
  FullState,
  ProfileDto,
  BookableWelcomeMeetingsResponse,
} from '@/types/remote/student.types';
import type {
  ArchiveEligibility,
  ArchiveRemoteUserDto,
  ArchiveRemoteUserResult,
  ReactivationEligibility,
  ReactivateRemoteUserResult,
  WithPhoneSetupLink,
} from '@/types/user.types';

/** `POST /students-remote` — create student + onboarding record. */
export const createProfile = async (
  dto: ProfileDto,
  companyId: string,
): Promise<{ user: unknown; record: unknown }> => {
  const response = await apiClient.post<{ user: unknown; record: unknown }>(
    `/students-remote?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** `PATCH /students-remote/:userId` */
export const updateProfile = async (
  userId: string,
  dto: ProfileDto,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> => {
  const response = await apiClient.patch<WithPhoneSetupLink<unknown>>(
    `/students-remote/${userId}?companyId=${companyId}`,
    dto,
  );
  return response.data;
};

/** Flatten onboarding filters into query params (`programId` skipped when `programIds` is set). */
function toListParams(
  filters: Omit<StudentFilters, 'companyId'> & { companyId?: string },
  companyId?: string,
): URLSearchParams {
  const params = companyId ? new URLSearchParams({ companyId }) : new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'companyId' || value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
      continue;
    }
    if (key === 'programId' && filters.programIds?.length) continue;
    params.append(key, String(value));
  }
  return params;
}

/** `GET /students-remote` — scoped list (no companyId required in the path). */
export async function listScoped(
  filters: Omit<StudentFilters, 'companyId'>,
): Promise<{ data: StudentRecord[]; total: number }> {
  const params = toListParams(filters);
  const { data } = await apiClient.get<{ data: StudentRecord[]; total: number }>(
    `/students-remote?${params}`,
  );
  return data;
}

/** `GET /onboarding/student-remote/:userId` — record + steps + templates. */
export async function getFullState(userId: string, companyId: string): Promise<FullState> {
  const { data } = await apiClient.get<FullState>(
    `/onboarding/student-remote/${userId}?companyId=${companyId}`,
  );
  return data;
}

/** `POST /onboarding/student-remote/:userId/welcome-meeting/participate` */
export async function markWelcomeMeetingParticipation(
  userId: string,
  companyId: string,
  sessionId?: string,
  participated: boolean = true,
): Promise<StudentRecord> {
  const { data } = await apiClient.post<StudentRecord>(
    `/onboarding/student-remote/${userId}/welcome-meeting/participate?companyId=${companyId}`,
    { sessionId, participated },
  );
  return data;
}

/** `PATCH /students-remote/:userId` with `sessionId: null` after canceling an RA booking. */
export async function clearWelcomeMeetingReservation(
  userId: string,
  companyId: string,
): Promise<WithPhoneSetupLink<unknown>> {
  return updateProfile(userId, { sessionId: null }, companyId);
}

/** `GET /onboarding/student-remote/welcome-meetings` — planned welcome meeting with a free student seat. */
export async function getBookableWelcomeMeetings(
  companyId: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<BookableWelcomeMeetingsResponse> {
  const params = new URLSearchParams({ companyId });
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  const { data } = await apiClient.get<BookableWelcomeMeetingsResponse>(
    `/onboarding/student-remote/welcome-meetings?${params}`,
  );
  return data;
}

/** `POST /onboarding/student-remote/:userId/welcome-meeting/reserve` */
export async function reserveWelcomeMeeting(
  userId: string,
  companyId: string,
  sessionId: string,
): Promise<StudentRecord> {
  const { data } = await apiClient.post<StudentRecord>(
    `/onboarding/student-remote/${userId}/welcome-meeting/reserve?companyId=${companyId}`,
    { sessionId },
  );
  return data;
}

/** `GET /onboarding/student-remote/export` — Excel blob. */
export async function exportExcel(
  filters: Omit<StudentFilters, 'companyId' | 'page' | 'pageSize'>,
  companyId: string,
): Promise<Blob> {
  const params = toListParams(filters, companyId);
  const { data } = await apiClient.get<Blob>(`/onboarding/student-remote/export?${params}`, {
    responseType: 'blob',
  });
  return data;
}

/** `GET /students-remote/:userId/archive-eligibility` */
export async function getArchiveEligibility(
  userId: string,
  companyId: string,
): Promise<ArchiveEligibility> {
  const { data } = await apiClient.get<ArchiveEligibility>(
    `/students-remote/${userId}/archive-eligibility?companyId=${companyId}`,
  );
  return data;
}

/** `POST /students-remote/:userId/archive` */
export async function archiveUser(
  userId: string,
  companyId: string,
  dto: ArchiveRemoteUserDto,
): Promise<ArchiveRemoteUserResult> {
  const { data } = await apiClient.post<ArchiveRemoteUserResult>(
    `/students-remote/${userId}/archive?companyId=${companyId}`,
    dto,
  );
  return data;
}

/** `GET /students-remote/:userId/reactivation-eligibility` */
export async function getReactivationEligibility(
  userId: string,
  companyId: string,
): Promise<ReactivationEligibility> {
  const { data } = await apiClient.get<ReactivationEligibility>(
    `/students-remote/${userId}/reactivation-eligibility?companyId=${companyId}`,
  );
  return data;
}

/** `POST /students-remote/:userId/reactivate` */
export async function reactivateUser(
  userId: string,
  companyId: string,
): Promise<ReactivateRemoteUserResult> {
  const { data } = await apiClient.post<ReactivateRemoteUserResult>(
    `/students-remote/${userId}/reactivate?companyId=${companyId}`,
    {},
  );
  return data;
}
